import { useAppSelector } from '@hooks/useAppSelector';
import { useAppDispatch } from '@hooks/useDispatch';
import { FileWithPath } from '@mantine/dropzone';
import { selectHideImages } from '@store/Spreadsheet/displaySlice';
import { setEditingCell } from '@store/Spreadsheet/editingSlice';
import { updateRow } from '@store/Spreadsheet/rowsSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CommonCellProps } from '../Cell';
import classes from '../Cell.module.css';
import VideoUploadModal from './VideoUploadModal';
import { Image } from '@mantine/core';
import { cellValueChanged } from '@store/Spreadsheet/cellChangesSlice';
import { IconPlayerPlay } from '@tabler/icons-react';

export default function CellVideo({ rowId, columnId, isEditing, value, currentRow }: CommonCellProps ) {
  const dispatch = useAppDispatch();
  const hideImages = useAppSelector( selectHideImages );

  const [ localValue, setLocalValue ] = useState<string>( value ?? '' );
  const [ thumbnailGenerated, setThumbnailGenerated ] = useState( false );
  const [ thumbnailUrl, setThumbnailUrl ] = useState<string | null>( null );
  const videoRef = useRef<HTMLVideoElement>( null );
 
  useEffect(() => {
    setLocalValue( String( value ?? '' ));
    // reset thumbnail state when value changes
    setThumbnailGenerated( false );
    setThumbnailUrl( null );
  }, [value]);

  // generate thumbnail for local videos
  useEffect(() => {
    const videoUrl = value || '';
    if (( videoUrl.startsWith( 'blob:' ) || videoUrl.endsWith( '.mp4' ) || videoUrl.endsWith( '.webm' ) || videoUrl.endsWith( '.ogg' )) && 
        !thumbnailGenerated && videoRef.current ) {
      const videoElement = videoRef.current;
      
      const handleCanPlay = () => {
        if ( videoElement.readyState >= 2 ) {
          videoElement.currentTime = Math.min( 1, videoElement.duration / 4 );
        }
      };
      
      const handleTimeUpdate = () => {
        if ( !thumbnailGenerated && videoElement.videoWidth > 0 ) {
          try {
            const canvas = document.createElement( 'canvas' );
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;
            const ctx = canvas.getContext( '2d' );
            if ( ctx ) {
              ctx.drawImage( videoElement, 0, 0, canvas.width, canvas.height );
              const dataUrl = canvas.toDataURL( 'image/jpeg' );
              setThumbnailUrl( dataUrl );
              setThumbnailGenerated( true );
              videoElement.removeEventListener( 'canplay', handleCanPlay );
              videoElement.removeEventListener( 'timeupdate', handleTimeUpdate );
            }
          } catch ( error ) {
            console.error( 'Error generating thumbnail:', error );
          }
        }
      };
      
      videoElement.addEventListener( 'canplay', handleCanPlay );
      videoElement.addEventListener( 'timeupdate', handleTimeUpdate );
      
      return () => {
        videoElement.removeEventListener( 'canplay', handleCanPlay );
        videoElement.removeEventListener( 'timeupdate', handleTimeUpdate );
      };
    }
  }, [ value, thumbnailGenerated ]);

  const commitEdit = useCallback(() => {
    if ( !currentRow ) return;
    const newValues = { ...currentRow.values, [columnId]: localValue };
    dispatch( updateRow({ id: rowId, changes: { values: newValues }}));
    dispatch( cellValueChanged({ rowId, columnId, oldValue: currentRow.values[columnId], newValue: localValue }));
    dispatch( setEditingCell( null ));
  }, [ dispatch, rowId, columnId, currentRow, localValue ]);

  const cancelEdit = useCallback(() => {
    dispatch( setEditingCell( null ));
  }, [dispatch]);
  
  const handleDrop = useCallback(( files: FileWithPath[]) => {
    if ( files.length > 0 ) {
      //TODO: aca hacer la carga temp a aws
      const objectUrl = URL.createObjectURL( files[0]);
      setLocalValue( objectUrl );
      // Reset thumbnail state for new video
      setThumbnailGenerated( false );
      setThumbnailUrl( null );
    }
  }, []);

const getVideoThumbnail = ( url: string ) => {
  if ( url.includes( 'youtube.com' ) || url.includes( 'youtu.be' )) {
    let videoId = '';
    if ( url.includes( 'youtube.com/watch?v=' )) {
      videoId = url.split( 'v=' )[1]?.split( '&' )[0] || '';
    } else if ( url.includes( 'youtu.be/' )) {
      videoId = url.split( 'youtu.be/' )[1]?.split( '?' )[0] || '';
    }
    if ( videoId ) {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  }
  
  if ( url.startsWith( 'blob:' ) || url.endsWith( '.mp4' ) || url.endsWith( '.webm' ) || url.endsWith( '.ogg' )) {
    return thumbnailUrl || url;
  }
  
  return 'https://placehold.co/80x80?text=Video';
};
  return (
    <>
      {/* hidden video element for thumbnail generation */}
      {value && ( value.startsWith( 'blob:' ) || value.endsWith( '.mp4' ) || value.endsWith( '.webm' ) || value.endsWith( '.ogg' )) && (
        <video 
          ref={videoRef} 
          src={value} 
          style={{ display: 'none' }} 
          preload="metadata"
        />
      )}
      
      {!isEditing ? (
        !value ? (
          <span className={classes.cellValue}>{value}</span>
        ) : hideImages ? (
          <span className={classes.cellValue}>{value}</span>
        ) : (
          <div
            className={classes.cellValue}
            style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
          >
           <Image
              src={getVideoThumbnail( value )}
              alt=""
              radius="sm"
              h={52}
              w={52}
              fit="contain"
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
                 
              <IconPlayerPlay size={14} color="white" />
            </div>
          </div>
        )
      ) : <span className={classes.cellValue}>{value}</span>}

     <VideoUploadModal
      isEditing={isEditing} 
      value={localValue}
      handleDrop={handleDrop}
      commitEdit={commitEdit}
      cancelEdit={cancelEdit}
     />
    </>
  );
}